/**
 * @fileoverview Hook for building navigation tree structure from data.
 * Pure transformation of data into navigation items.
 */

import { useMemo, createElement } from 'react';
import { Person as PersonIcon } from '@mui/icons-material';
import { BiArea } from 'react-icons/bi';
import { PiFolder, PiPencilRuler } from 'react-icons/pi';
import { STATIC_NAVIGATION, ADMIN_NAVIGATION } from '@/config/navigation';
import type { Area, Project, Resource } from '@/api';
import type { Navigation } from '../types';

export interface NavigationBuilderInput {
  projects: Project[];
  resources: Resource[];
  areas: Area[];
  dataLoaded: boolean;
}

/**
 * Builds the navigation tree structure from API data.
 * Separates the pure transformation logic from data fetching.
 */
export function useNavigationBuilder(input: NavigationBuilderInput): Navigation {
  const { projects, resources, areas, dataLoaded } = input;

  return useMemo(() => {
    const projectChildren = dataLoaded
      ? projects.map((project) => ({
          segment: `${project.id}`,
          title: project.name,
          pattern: `${project.id}`,
          href: `/${project.id}`,
        }))
      : [{ segment: 'loading', title: 'Loading...', pattern: 'loading' }];

    const resourceChildren = dataLoaded
      ? resources.map((resource) => ({
          segment: `${resource.id}`,
          title: resource.name,
          pattern: `resources/${resource.id}`,
          href: `/resources/${resource.id}`,
        }))
      : [{ segment: 'loading', title: 'Loading...', pattern: 'loading' }];

    const areaChildren = dataLoaded
      ? areas.map((area) => ({
          segment: `${area.id}`,
          title: area.name,
          pattern: `areas/${area.id}`,
          href: `/areas/${area.id}`,
        }))
      : [{ segment: 'loading', title: 'Loading...', pattern: 'loading' }];

    const paraNavItems: Navigation = [
      { kind: 'header', title: 'PARA' },
      {
        segment: 'projects',
        title: 'Projects',
        icon: createElement(PiPencilRuler, { size: 21 }),
        pattern: 'projects',
        children: projectChildren,
      },
      {
        segment: 'areas',
        title: 'Areas',
        icon: createElement(BiArea, { size: 21 }),
        pattern: 'areas',
        children: areaChildren,
      },
      {
        segment: 'resources',
        title: 'Resources',
        icon: createElement(PiFolder, { size: 21 }),
        pattern: 'resources',
        children: resourceChildren,
      },
    ];

    const peopleNavItems: Navigation = [
      { kind: 'header', title: 'People' },
      {
        segment: 'people',
        title: 'People',
        icon: createElement(PersonIcon),
        pattern: 'people{/:personId}',
      },
    ];

    return [
      ...STATIC_NAVIGATION,
      ...paraNavItems,
      ...peopleNavItems,
      ...ADMIN_NAVIGATION,
    ];
  }, [projects, resources, areas, dataLoaded]);
}

